// frontend/src/components/staff/ScheduleBuilderGrid.js
//
// Workforce — SCHED-SLICE1-E weekly calendar grid (admin).
//
// Visual spec: docs/design/workforce-scheduling-slice-1-design.md §3.1 +
// §3.2 + §3.3 + §11.1 (empty state) + visual DoD §18.1.
//
// 7 day-columns × hourly rows (6am-10pm default; visible hours from
// `scheduleBuilderUtils.DEFAULT_GRID_START_HOUR` / `..END_HOUR`).
//
// Renders shift blocks as absolutely-positioned overlays per day-column
// using `computeShiftBlockGeometry`. Cell click + drag interactions
// dispatch:
//   - onCellClick({ dayIso, minuteOfDay })     — opens New Shift modal
//   - onCellDragCreate({ dayIso, startMin, endMin }) — opens New Shift
//                                                     modal with range
//   - onShiftClick(shift)                      — opens Edit Shift modal
//   - onShiftDragMove({ shift, dayIso, startMin, endMin }) — fires PUT
//   - onShiftResize({ shift, endMin })         — fires PUT with new end
//
// Drag pickup uses a 150ms hold (visual spec §3.3); snap target is 15
// minutes (`SNAP_MINUTES`).
//
// Keyboard alternative to drag (visual spec §17): focused shift block
// + Arrow keys = move (parent-bound onShiftClick handles Enter; this
// component does not implement arrow-key movement in v1 — the visual
// spec accepts that arrow-key DnD is a stretch goal for Slice 1).

import React from "react";
import {
  DEFAULT_GRID_START_HOUR,
  DEFAULT_GRID_END_HOUR,
  HOUR_ROW_HEIGHT_PX,
  DRAG_PICKUP_MS,
  buildHourRows,
  buildWeekDays,
  computeShiftBlockGeometry,
  groupShiftsByDay,
  yPxToMinuteOfDay,
  SNAP_MINUTES,
  // SCHED-OVERLAP-COLUMNS — overlap detection + column-layout math.
  // Visual spec: docs/design/SCHED-OVERLAP-COLUMNS-design.md §2 + §5.
  computeOverlapGroups,
  computeOverlapColumnWidth,
  shouldBailToStackRow,
} from "./scheduleBuilderUtils";
import ScheduleShiftBlock from "./ScheduleShiftBlock";
import "./ScheduleBuilderGrid.css";

const ScheduleBuilderGrid = ({
  weekStartYmd,
  shifts,
  staffById,
  startHour = DEFAULT_GRID_START_HOUR,
  endHour = DEFAULT_GRID_END_HOUR,
  filterStaffProfileId,
  onCellClick,
  onCellDragCreate,
  onShiftClick,
  onShiftDragMove,
  conflictHighlightShiftId,
  isEmpty,
}) => {
  const weekDays = React.useMemo(() => buildWeekDays(weekStartYmd), [weekStartYmd]);
  const hourRows = React.useMemo(() => buildHourRows(startHour, endHour), [startHour, endHour]);
  const dayColumnsRef = React.useRef(new Map());

  // ---- Day-cell width measurements (SCHED-OVERLAP-COLUMNS) ----------------
  // Track measured pixel widths of each day cell so the overlap-column
  // layout can decide text mode + stack-row bail accurately. Initial
  // render falls back to a sentinel (0) which the helpers treat as
  // "unknown — use single-shift defaults". Once ResizeObserver fires,
  // the per-day width populates and the column layout takes effect.
  const [dayCellWidths, setDayCellWidths] = React.useState({});
  const dayCellResizeObserverRef = React.useRef(null);

  // Single ResizeObserver per grid lifetime — observes all 7 day cells.
  // Tear-down on unmount or weekStart change keeps the observer scoped
  // to the currently rendered week.
  React.useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      let nextUpdates = null;
      for (const entry of entries) {
        const dayIso = entry.target && entry.target.dataset && entry.target.dataset.dayIso;
        if (!dayIso) continue;
        const width = entry.contentRect && entry.contentRect.width;
        if (!Number.isFinite(width)) continue;
        if (!nextUpdates) nextUpdates = {};
        nextUpdates[dayIso] = width;
      }
      if (nextUpdates) {
        setDayCellWidths((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const [k, v] of Object.entries(nextUpdates)) {
            if (prev[k] !== v) {
              next[k] = v;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    });
    dayCellResizeObserverRef.current = observer;
    // Observe whatever day cells are already in the ref Map (the
    // callback ref below also subscribes new cells as they mount).
    for (const el of dayColumnsRef.current.values()) {
      if (el) observer.observe(el);
    }
    return () => {
      observer.disconnect();
      dayCellResizeObserverRef.current = null;
    };
  }, [weekStartYmd]);

  // Callback ref factory that stores the day-cell element AND subscribes
  // it to the ResizeObserver. Mounting a new cell that arrives after the
  // observer was created (e.g., toggling staff filter) still gets
  // observed.
  const setDayColumnRef = React.useCallback(
    (dayIso) => (el) => {
      if (el) {
        dayColumnsRef.current.set(dayIso, el);
        el.dataset.dayIso = dayIso;
        if (dayCellResizeObserverRef.current) {
          dayCellResizeObserverRef.current.observe(el);
        }
        // Seed an initial measurement so the first render has a
        // non-zero width even before the observer fires.
        setDayCellWidths((prev) => {
          if (Number.isFinite(prev[dayIso]) && prev[dayIso] > 0) return prev;
          const rect = el.getBoundingClientRect();
          if (!rect || !Number.isFinite(rect.width) || rect.width <= 0) return prev;
          return { ...prev, [dayIso]: rect.width };
        });
      }
    },
    []
  );

  // ---- Visible shifts after staff-filter ----------------------------------
  const visibleShifts = React.useMemo(() => {
    if (!filterStaffProfileId) return shifts;
    return (shifts || []).filter(
      (s) => String(s.staffProfileId) === String(filterStaffProfileId)
    );
  }, [shifts, filterStaffProfileId]);

  const shiftsByDay = React.useMemo(() => groupShiftsByDay(visibleShifts), [visibleShifts]);

  // ---- Drag state ---------------------------------------------------------
  // Two drag modes:
  //   - 'create' = mousedown on empty cell -> drag down -> mouseup ->
  //                opens New Shift modal with range
  //   - 'move'   = mousedown on shift block -> after 150ms hold -> drag
  //                anywhere -> mouseup -> fires onShiftDragMove
  const [drag, setDrag] = React.useState(null);
  const dragHoldTimer = React.useRef(null);
  const startedMoveRef = React.useRef(false);

  const cancelDragHold = () => {
    if (dragHoldTimer.current) {
      clearTimeout(dragHoldTimer.current);
      dragHoldTimer.current = null;
    }
  };

  // Compute (dayIso, minuteOfDay) from a clientX/clientY pair.
  const pointToDayAndMinute = React.useCallback(
    (clientX, clientY, mode = "round") => {
      // Find which day-column contains clientX.
      let dayIso = null;
      let columnEl = null;
      for (const day of weekDays) {
        const el = dayColumnsRef.current.get(day.iso);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right) {
          dayIso = day.iso;
          columnEl = el;
          break;
        }
      }
      if (!dayIso || !columnEl) return null;
      const colRect = columnEl.getBoundingClientRect();
      const y = clientY - colRect.top;
      const minuteOfDay = yPxToMinuteOfDay(y, startHour, mode);
      return { dayIso, minuteOfDay };
    },
    [weekDays, startHour]
  );

  // ---- Pointer / global handlers ------------------------------------------
  React.useEffect(() => {
    if (!drag) return undefined;

    const onMove = (e) => {
      const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
      const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
      if (clientX === undefined || clientY === undefined) return;

      if (drag.mode === "create") {
        const point = pointToDayAndMinute(clientX, clientY);
        if (!point) return;
        // Keep create within the originating column.
        if (point.dayIso !== drag.dayIso) return;
        setDrag((prev) => (prev ? { ...prev, currentMin: point.minuteOfDay } : prev));
      } else if (drag.mode === "move-pending" || drag.mode === "move") {
        // Promote to active move once threshold met.
        const dx = Math.abs(clientX - drag.startClientX);
        const dy = Math.abs(clientY - drag.startClientY);
        if (drag.mode === "move-pending" && (dx > 4 || dy > 4 || startedMoveRef.current)) {
          startedMoveRef.current = true;
          // Drag pickup activated via hold timer or threshold.
          setDrag((prev) => (prev ? { ...prev, mode: "move", currentClientX: clientX, currentClientY: clientY } : prev));
          return;
        }
        const point = pointToDayAndMinute(clientX, clientY, "floor");
        if (!point) return;
        setDrag((prev) =>
          prev ? { ...prev, currentDayIso: point.dayIso, currentMin: point.minuteOfDay } : prev
        );
      }
    };

    const onUp = () => {
      cancelDragHold();
      if (!drag) return;

      if (drag.mode === "create" && drag.currentMin !== undefined && drag.currentMin !== drag.startMin) {
        const a = Math.min(drag.startMin, drag.currentMin);
        const b = Math.max(drag.startMin, drag.currentMin);
        // Require at least SNAP_MINUTES of duration.
        if (b - a >= SNAP_MINUTES) {
          if (typeof onCellDragCreate === "function") {
            onCellDragCreate({ dayIso: drag.dayIso, startMin: a, endMin: b });
          }
        }
      } else if (drag.mode === "move" && drag.shift) {
        // Compute new placement.
        const durationMin =
          (new Date(drag.shift.endTime).getTime() - new Date(drag.shift.startTime).getTime()) / 60000;
        const newStartMin = drag.currentMin !== undefined ? drag.currentMin : null;
        const newDayIso = drag.currentDayIso || drag.dayIso;
        if (newStartMin !== null && typeof onShiftDragMove === "function") {
          const newEndMin = newStartMin + durationMin;
          onShiftDragMove({
            shift: drag.shift,
            dayIso: newDayIso,
            startMin: newStartMin,
            endMin: newEndMin,
          });
        }
      }
      setDrag(null);
      startedMoveRef.current = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [drag, pointToDayAndMinute, onCellDragCreate, onShiftDragMove]);

  // ---- Empty-cell pointer handler -----------------------------------------
  const handleCellMouseDown = (dayIso) => (e) => {
    if (e.target && e.target.closest && e.target.closest(".schedule-shift-block")) {
      return; // shift block owns the gesture
    }
    const clientX = e.clientX || (e.touches && e.touches[0] && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0] && e.touches[0].clientY);
    const point = pointToDayAndMinute(clientX, clientY, "floor");
    if (!point) return;
    setDrag({
      mode: "create",
      dayIso: point.dayIso,
      startMin: point.minuteOfDay,
      currentMin: point.minuteOfDay,
      startClientX: clientX,
      startClientY: clientY,
    });
  };

  // Click without drag = open new-shift modal at that cell.
  const handleCellClick = (dayIso) => (e) => {
    if (e.target && e.target.closest && e.target.closest(".schedule-shift-block")) {
      return;
    }
    if (drag) return; // drag-in-progress consumes the click
    const clientX = e.clientX;
    const clientY = e.clientY;
    const point = pointToDayAndMinute(clientX, clientY, "floor");
    if (!point) return;
    if (typeof onCellClick === "function") {
      onCellClick({ dayIso: point.dayIso, minuteOfDay: point.minuteOfDay });
    }
  };

  // ---- Shift block handlers -----------------------------------------------
  const handleShiftPointerDown = ({ shift, event }) => {
    const clientX = event.clientX || (event.touches && event.touches[0] && event.touches[0].clientX);
    const clientY = event.clientY || (event.touches && event.touches[0] && event.touches[0].clientY);
    if (clientX === undefined) return;
    startedMoveRef.current = false;
    setDrag({
      mode: "move-pending",
      shift,
      dayIso: undefined,
      currentDayIso: undefined,
      currentMin: undefined,
      startClientX: clientX,
      startClientY: clientY,
    });
    cancelDragHold();
    dragHoldTimer.current = setTimeout(() => {
      // Auto-activate after the pickup hold even if pointer hasn't moved
      // enough — matches visual spec §3.3 ("150ms hold = drag pickup").
      startedMoveRef.current = true;
      setDrag((prev) => (prev ? { ...prev, mode: "move" } : prev));
    }, DRAG_PICKUP_MS);
  };

  // ---- Render -------------------------------------------------------------
  const gridHeightPx = (endHour - startHour) * HOUR_ROW_HEIGHT_PX;

  return (
    <div className="schedule-builder-grid-wrapper">
      <div
        className="schedule-builder-grid"
        role="grid"
        aria-label={`Schedule builder week of ${weekStartYmd}`}
        data-testid="schedule-builder-grid"
      >
        <div className="schedule-builder-grid__hour-gutter" aria-hidden="true">
          <div className="schedule-builder-grid__day-header schedule-builder-grid__day-header--gutter" />
          {hourRows.map((row) => (
            <div
              key={row.hour}
              className="schedule-builder-grid__hour-label"
              style={{ height: HOUR_ROW_HEIGHT_PX }}
            >
              {row.label}
            </div>
          ))}
        </div>

        {weekDays.map((day) => {
          const dayShifts = shiftsByDay.get(day.iso) || [];
          // SCHED-OVERLAP-COLUMNS — compute overlap groups for this day
          // up front so each shift render call has its (columnIndex,
          // groupSize) pair already resolved. The Map keyed by shift._id
          // is O(N) to construct and avoids re-running the sweep inside
          // the render loop. Group sizes are also used to derive the
          // "stack-row index" for any group that bails to the fallback.
          const overlapGroups = computeOverlapGroups(dayShifts);
          const dayCellWidthPx = Number(dayCellWidths[day.iso]) || 0;
          // Per-group stack-row counter — only used when bail triggers.
          const stackRowCursorByGroup = new Map();
          return (
            <div
              key={day.iso}
              ref={setDayColumnRef(day.iso)}
              className={
                "schedule-builder-grid__day-column" +
                (day.isToday ? " schedule-builder-grid__day-column--today" : "") +
                (day.isPast ? " schedule-builder-grid__day-column--past" : "")
              }
              role="gridcell"
              aria-label={`${day.weekday} ${day.monthLabel} ${day.dayLabel}`}
              data-testid={`schedule-builder-grid-day-${day.iso}`}
            >
              <div className="schedule-builder-grid__day-header">
                <span className="schedule-builder-grid__day-weekday">{day.weekday}</span>
                <span className="schedule-builder-grid__day-date">
                  {day.monthLabel} {day.dayLabel}
                </span>
              </div>
              <div
                className="schedule-builder-grid__day-body"
                style={{ height: gridHeightPx }}
                onMouseDown={handleCellMouseDown(day.iso)}
                onTouchStart={handleCellMouseDown(day.iso)}
                onClick={handleCellClick(day.iso)}
              >
                {hourRows.map((row, i) => (
                  <div
                    key={row.hour}
                    className="schedule-builder-grid__hour-row"
                    style={{ top: i * HOUR_ROW_HEIGHT_PX, height: HOUR_ROW_HEIGHT_PX }}
                    aria-hidden="true"
                  />
                ))}

                {drag &&
                  drag.mode === "create" &&
                  drag.dayIso === day.iso &&
                  drag.startMin !== undefined &&
                  drag.currentMin !== undefined && (
                    <div
                      className="schedule-builder-grid__drag-rubber"
                      style={{
                        top:
                          ((Math.min(drag.startMin, drag.currentMin) - startHour * 60) / 60) *
                          HOUR_ROW_HEIGHT_PX,
                        height:
                          (Math.abs(drag.currentMin - drag.startMin) / 60) * HOUR_ROW_HEIGHT_PX,
                      }}
                      data-testid={`schedule-builder-grid-drag-rubber-${day.iso}`}
                    />
                  )}

                {dayShifts.map((shift) => {
                  const geometry = computeShiftBlockGeometry(shift, startHour, endHour);
                  if (!geometry) return null;
                  // SCHED-OVERLAP-COLUMNS — resolve this shift's layout
                  // mode, columnIndex, groupSize. Bail to stacked-row
                  // when groupSize >= 4 OR the per-column width would
                  // fall under the legibility floor (40px). Single
                  // shifts and non-overlapping groups (groupSize === 1)
                  // render at full width per slice-1 defaults.
                  const groupEntry =
                    overlapGroups.get(String(shift._id || `__anon_${shift.startTime}`)) ||
                    { columnIndex: 0, groupSize: 1 };
                  let layout;
                  if (groupEntry.groupSize <= 1) {
                    layout = {
                      mode: "single",
                      columnIndex: 0,
                      groupSize: 1,
                      dayCellWidthPx,
                    };
                  } else {
                    const colW = computeOverlapColumnWidth(dayCellWidthPx, groupEntry.groupSize);
                    const bail =
                      dayCellWidthPx > 0 // only consider bail once measured
                        ? shouldBailToStackRow({ groupSize: groupEntry.groupSize, columnWidthPx: colW })
                        : groupEntry.groupSize >= 4; // unmeasured: bail only on N>=4
                    if (bail) {
                      // Assign a stack-row index within this group. We
                      // can't read it off `groupEntry.columnIndex`
                      // directly because the bail path may have
                      // multiple shifts at the same vertical anchor;
                      // a stable per-group cursor preserves the
                      // createdAt-asc order from `computeOverlapGroups`.
                      const groupKey = `g-${String(shift._id)}-${groupEntry.columnIndex}`;
                      // Use columnIndex as the stack-row index — the
                      // helper has already sorted by createdAt-asc + _id.
                      stackRowCursorByGroup.set(groupKey, groupEntry.columnIndex);
                      layout = {
                        mode: "stack-row",
                        columnIndex: 0,
                        groupSize: groupEntry.groupSize,
                        stackRowIndex: groupEntry.columnIndex,
                        stackRowCount: groupEntry.groupSize,
                        dayCellWidthPx,
                      };
                    } else {
                      layout = {
                        mode: "column",
                        columnIndex: groupEntry.columnIndex,
                        groupSize: groupEntry.groupSize,
                        dayCellWidthPx,
                      };
                    }
                  }
                  return (
                    <ScheduleShiftBlock
                      key={shift._id}
                      shift={shift}
                      staffById={staffById}
                      geometry={geometry}
                      layout={layout}
                      onClick={onShiftClick}
                      onPointerDown={handleShiftPointerDown}
                      isDragging={
                        drag &&
                        drag.shift &&
                        String(drag.shift._id) === String(shift._id) &&
                        drag.mode === "move"
                      }
                      isConflictHighlighted={
                        conflictHighlightShiftId &&
                        String(conflictHighlightShiftId) === String(shift._id)
                      }
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {isEmpty && (
        <div
          className="schedule-builder-grid__empty-state"
          role="status"
          data-testid="schedule-builder-grid-empty"
        >
          <div className="schedule-builder-grid__empty-card">
            <span className="schedule-builder-grid__empty-icon" aria-hidden="true">
              {String.fromCharCode(0x1f4c5)}
            </span>
            <h3 className="schedule-builder-grid__empty-title">No shifts yet for this week</h3>
            <p className="schedule-builder-grid__empty-body">
              Start by clicking on an empty cell to add a shift.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleBuilderGrid;
